import { ENGINE_PROVENANCE } from "../engine/provenance";
import { findTctScenario } from "../tct-demo/scenarios";
import { simulateTctScenario } from "../tct-demo/simulator";
import {
  TCT_CURL,
  TCT_MURL,
  TCT_MURL_PROFILE,
  STORY_BASE_JSON,
  STORY_BASE_VALIDATORS,
  STORY_AST_CORE_PROFILE,
  STORY_BRANCHES,
  STORY_MERGED_VALIDATORS,
  STORY_SBR,
  STORY_VALIDATOR_ROWS,
  storyBranch,
  storyJson,
} from "./fixtures";
import type {
  StoryPhase,
  StorySnapshot,
  StoryTimelineStep,
  StoryValidatorRow,
} from "./types";
import { TCT_MURL_IDENTITY } from "../tct-demo/fixtures";

const PHASE_RANK: Readonly<Record<StoryPhase, number>> = {
  ready: 0,
  tct_verified: 1,
  sbr_observed: 2,
  source_prepared: 3,
  concurrent_committed: 4,
  precondition_failed: 5,
  target_refreshed: 6,
  merge_pending: 7,
  merged_ready: 8,
  conflict: 8,
  stopped: 8,
  committed: 9,
};

const encoder = new TextEncoder();

function byteLength(value: string): number {
  return encoder.encode(value).byteLength;
}

function reached(snapshot: StorySnapshot, phase: StoryPhase): boolean {
  return PHASE_RANK[snapshot.phase] >= PHASE_RANK[phase];
}

function sbrRead(
  body: string,
  etag: string,
  semanticEtag: string,
): string {
  return [
    "GET /api/article/123 HTTP/1.1",
    "Host: example.com",
    "Accept: application/json",
    "Accept-Encoding: identity",
    "",
    "HTTP/1.1 200 OK",
    "Content-Type: application/json",
    `Content-Length: ${byteLength(body)}`,
    `ETag: ${etag}`,
    `Semantic-ETag: ${semanticEtag}`,
    `Link: <${STORY_AST_CORE_PROFILE}>; rel="profile"`,
    "",
    body,
  ].join("\n");
}

function tctDiscoveryWire(): string {
  const scenario = findTctScenario("catalog-discovery");
  if (scenario === undefined) {
    throw new Error("Published TCT catalog scenario is missing.");
  }
  const result = simulateTctScenario(scenario);
  return result.steps.map(({ wire }) => wire).join("\n\n---\n\n");
}

function tctIdentityWire(): string {
  return [
    "GET /article/123/llm.json HTTP/1.1",
    "Host: example.com",
    "Accept: application/json",
    "Accept-Encoding: identity",
    "",
    "HTTP/1.1 200 OK",
    "Content-Type: application/json",
    `Content-Length: ${byteLength(TCT_MURL_IDENTITY)}`,
    `ETag: ${STORY_BASE_VALIDATORS.murlEtag}`,
    `Semantic-ETag: ${STORY_BASE_VALIDATORS.semanticEtag}`,
    `Link: <${TCT_CURL}>; rel="canonical"`,
    `Link: <${TCT_MURL_PROFILE}>; rel="profile"`,
    `Link: <${STORY_SBR}>; rel="concurrency-state"; type="application/json"`,
    "",
    TCT_MURL_IDENTITY,
  ].join("\n");
}

function stalePut(snapshot: StorySnapshot): string {
  const body = storyJson(storyBranch(snapshot.branch).source);
  const targetValidators = storyBranch(snapshot.branch).targetValidators;
  const problem =
    '{"type":"https://example.com/problems/precondition-failed","title":"Precondition failed","status":412}';
  return [
    "PUT /api/article/123 HTTP/1.1",
    "Host: example.com",
    "Content-Type: application/json",
    `Content-Length: ${byteLength(body)}`,
    `If-Match: ${STORY_BASE_VALIDATORS.sbrEtag}`,
    "",
    body,
    "",
    "HTTP/1.1 412 Precondition Failed",
    "Content-Type: application/problem+json",
    `Content-Length: ${byteLength(problem)}`,
    `ETag: ${targetValidators.sbrEtag}`,
    `Semantic-ETag: ${targetValidators.semanticEtag}`,
    `Link: <${STORY_SBR}>; rel="concurrency-state"; type="application/json"`,
    "",
    problem,
  ].join("\n");
}

function committedPut(snapshot: StorySnapshot): string {
  if (snapshot.mergeResult?.response.status !== "merged") {
    return "No complete merge candidate is available.";
  }
  const body = JSON.stringify(snapshot.mergeResult.response.merged);
  const targetValidators = storyBranch(snapshot.branch).targetValidators;
  return [
    "PUT /api/article/123 HTTP/1.1",
    "Host: example.com",
    "Content-Type: application/json",
    `Content-Length: ${byteLength(body)}`,
    `If-Match: ${targetValidators.sbrEtag}`,
    "",
    body,
    "",
    "HTTP/1.1 204 No Content",
    `ETag: ${STORY_MERGED_VALIDATORS.sbrEtag}`,
    `Semantic-ETag: ${STORY_MERGED_VALIDATORS.semanticEtag}`,
  ].join("\n");
}

export function storyValidatorRows(
  snapshot: StorySnapshot,
): readonly StoryValidatorRow[] {
  if (snapshot.branch === "merge") {
    return STORY_VALIDATOR_ROWS;
  }
  const target = STORY_BRANCHES.conflict.targetValidators;
  return [
    STORY_VALIDATOR_ROWS[0]!,
    STORY_VALIDATOR_ROWS[1]!,
    {
      state: "Target",
      sbrEtag: target.sbrEtag ?? "none",
      murlEtag: target.murlEtag ?? "none",
      semanticEtag: target.semanticEtag ?? "none",
      meaning: "Title changed, so both selected representations advance.",
    },
    {
      state: "Merged",
      sbrEtag: "none",
      murlEtag: "none",
      semanticEtag: "none",
      meaning: "Conflict: no merged state or server validator exists.",
    },
  ];
}

export function storyTimeline(
  snapshot: StorySnapshot,
): readonly StoryTimelineStep[] {
  const fixture = storyBranch(snapshot.branch);
  const steps: StoryTimelineStep[] = [
    {
      number: "00",
      eyebrow: "Application contract",
      title: "Bind the complete state before composing protocols",
      detail:
        "The SBR is one closed array-free JSON object. Whole-resource PUT replaces it, and the fixed application policy must accept any merge candidate before confirmation appears.",
      wire: [
        `Canonical resource: ${TCT_CURL}`,
        `TCT M-URL: ${TCT_MURL}`,
        `AST SBR: ${STORY_SBR}`,
        "SBR schema: summary + title + workflow",
        `Selected branch: ${fixture.label}`,
      ].join("\n"),
      tone: "neutral",
    },
  ];

  if (reached(snapshot, "tct_verified")) {
    steps.push(
      {
        number: "01",
        eyebrow: "TCT discovery",
        title: "Confirm the publisher-selected M-Sitemap",
        detail:
          "The existing deterministic TCT simulator supplies candidate discovery and exact Draft-03 catalog confirmation.",
        wire: tctDiscoveryWire(),
        tone: "success",
      },
      {
        number: "02",
        eyebrow: "TCT + semantic observation",
        title: "Certify bytes, then keep semantic state separate",
        detail:
          "The M-URL ordinary ETag binds its exact bytes. The equal semantic validator and concurrency-state link are separately declared by the origin application.",
        wire: tctIdentityWire(),
        tone: "success",
      },
    );
  }

  if (reached(snapshot, "sbr_observed")) {
    steps.push({
      number: "03",
      eyebrow: "AST state discovery",
      title: "Read the complete State-Bearing Representation",
      detail:
        "The SBR has its own strong ordinary ETag. This is the validator retained for AST Core writes at this target.",
      wire: sbrRead(
        STORY_BASE_JSON,
        STORY_BASE_VALIDATORS.sbrEtag ?? "",
        STORY_BASE_VALIDATORS.semanticEtag ?? "",
      ),
      tone: "success",
    });
  }

  if (reached(snapshot, "source_prepared")) {
    steps.push({
      number: "04",
      eyebrow: "Local intent",
      title: "Construct one complete source value",
      detail:
        "The source is derived from the retained base. It is local only and receives no invented server validator.",
      wire: [
        "Local operation: no HTTP request",
        "Base:",
        STORY_BASE_JSON,
        "",
        "Source:",
        storyJson(fixture.source),
      ].join("\n"),
      tone: "neutral",
    });
  }

  if (reached(snapshot, "concurrent_committed")) {
    steps.push({
      number: "05",
      eyebrow: "Viewer-only event",
      title: "An intervening writer advances the domain",
      detail:
        "The viewer sees this atomic commit. The simulated client remains unaware and still holds the base SBR ETag.",
      wire: [
        "External atomic commit: 1",
        `Previous SBR ETag: ${STORY_BASE_VALIDATORS.sbrEtag}`,
        `Current SBR ETag: ${fixture.targetValidators.sbrEtag}`,
        `Current Semantic-ETag: ${fixture.targetValidators.semanticEtag}`,
        "Client knowledge: still base",
      ].join("\n"),
      tone: "warning",
    });
  }

  if (reached(snapshot, "precondition_failed")) {
    steps.push({
      number: "06",
      eyebrow: "AST Core precondition",
      title: "Reject the stale whole-resource PUT",
      detail:
        "The base SBR ETag no longer matches. The 412 response changes no state, and its current validators remain advisory until a fresh read.",
      wire: stalePut(snapshot),
      tone: "rejected",
    });
  }

  if (reached(snapshot, "target_refreshed")) {
    steps.push({
      number: "07",
      eyebrow: "Fresh state",
      title: "Retrieve the current target before reconciliation",
      detail:
        "The client now learns the target state and retains its current SBR ETag for a possible later retry.",
      wire: sbrRead(
        storyJson(fixture.target),
        fixture.targetValidators.sbrEtag ?? "",
        fixture.targetValidators.semanticEtag ?? "",
      ),
      tone: "success",
    });
  }

  if (snapshot.phase === "merge_pending") {
    steps.push({
      number: "08",
      eyebrow: "Pinned worker",
      title: "Run the exact base/source/target triple",
      detail:
        "The bounded worker is active. Resetting or changing branches invalidates this run and any late response.",
      wire: [
        `Engine: ${ENGINE_PROVENANCE.engine}`,
        `Package: ${ENGINE_PROVENANCE.package}@${ENGINE_PROVENANCE.version}`,
        `Run: ${snapshot.runId}`,
        "Status: pending",
      ].join("\n"),
      tone: "warning",
    });
  }

  if (snapshot.mergeResult !== null && snapshot.phase !== "merge_pending") {
    steps.push({
      number: "08",
      eyebrow: "Pinned merge result",
      title:
        snapshot.mergeResult.response.status === "merged"
          ? "Receive one complete candidate"
          : "Receive the complete ordered conflict set",
      detail:
        snapshot.mergeResult.response.status === "merged"
          ? "The worker returned a complete value. It still needs the fixed schema and application-policy gate."
          : "The worker returned /title with no partial merged object. The story cannot retry or commit.",
      wire: snapshot.mergeResult.raw,
      tone:
        snapshot.mergeResult.response.status === "merged"
          ? "success"
          : "rejected",
    });
  }

  if (snapshot.policyResult !== null) {
    steps.push({
      number: "09",
      eyebrow: "Application gate",
      title: snapshot.policyResult.passed
        ? "Validate the complete candidate"
        : "Reject the candidate",
      detail: snapshot.policyResult.reason,
      wire: snapshot.policyResult.checks.join("\n"),
      tone: snapshot.policyResult.passed ? "success" : "rejected",
    });
  }

  if (snapshot.phase === "conflict") {
    steps.push({
      number: "09",
      eyebrow: "Resolution boundary",
      title: "Stop for human or application policy",
      detail:
        "Merge reports the conflict but does not resolve it, grant approval, or authorize an external action.",
      wire: [
        "Retry: not available",
        "Partial merged value: none",
        "Client commits: 0",
        "Required next step: intervention outside this demo",
      ].join("\n"),
      tone: "rejected",
    });
  }

  if (snapshot.phase === "committed") {
    steps.push({
      number: "10",
      eyebrow: "Explicit simulation confirmation",
      title: "Retry once against the freshly read SBR ETag",
      detail:
        "Confirmation advances this local educational story only. The condition, complete replacement, and validator publication occur as one simulated atomic commit.",
      wire: committedPut(snapshot),
      tone: "success",
    });
  }

  if (snapshot.phase === "stopped") {
    steps.push({
      number: "STOP",
      eyebrow: "Fail closed",
      title: "Do not expose retry or commit eligibility",
      detail: snapshot.error ?? "The active run stopped.",
      wire: [
        `Run: ${snapshot.runId}`,
        "Confirmation: unavailable",
        "Retry: not sent",
        `Client commits: ${snapshot.clientCommitCount}`,
      ].join("\n"),
      tone: "rejected",
    });
  }

  return steps;
}
